import React from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import NextRouter, { useRouter } from 'next/router'
import usePrevious from '@jarvis.oddle/core/hooks/usePrevious'

class ModalController {
  _modals: Record<string, Record<string, unknown>>
  _subscribers: Record<string, (x) => void>

  constructor() {
    this._modals = {}
    this._subscribers = {}
  }

  _notifySubscriber = (modalName) => {
    this._subscribers[modalName] &&
      this._subscribers[modalName](this._modals[modalName] || {})
  }

  isOpen = (modalName) =>
    this._modals[modalName] && this._modals[modalName].show

  registerModal = (modalName, subscribe, show = false) => {
    this._modals[modalName] = { show }
    this._subscribers[modalName] = subscribe
  }

  showModal = (modalName, forwardProps?) => {
    this._modals[modalName] = {
      ...this._modals[modalName],
      show: true,
      forwardProps
    }
    this._notifySubscriber(modalName)
  }

  closeModal = (modalName, onClosedCallback?) => {
    if (typeof onClosedCallback !== 'function') {
      onClosedCallback = () => undefined
    }
    this._modals[modalName] = {
      ...this._modals[modalName],
      show: false,
      onClosedCallback
    }
    this._notifySubscriber(modalName)
  }
}

const modalController = new ModalController()

const modalReducer = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_SHOW_STATE':
      return {
        ...action.payload,
        forwardProps: { ...state.forwardProps, ...action.payload.forwardProps }
      }
    default:
      return state
  }
}

export const makeControlledModal = (
  Modal,
  modalName: string
): (({
  show,
  ...ownProps
}: {
  show?: boolean
  [ownProps: string]: unknown
}) => JSX.Element) => {
  const ControlledModal = ({
    show,
    ...ownProps
  }: {
    show?: boolean
    [ownProps: string]: unknown
  }): JSX.Element => {
    const [state, dispatch] = React.useReducer(modalReducer, {
      show
    })

    const previousModal = React.useRef(null)
    const replaceMode = React.useRef(false)

    React.useEffect(() => {
      const handleModalUpdate = ({
        show,
        forwardProps,
        onClosedCallback = () => undefined
      }) => {
        previousModal.current = forwardProps?.previousModal || null
        replaceMode.current = forwardProps?.replaceMode || false
        dispatch({
          type: 'TOGGLE_SHOW_STATE',
          payload: { show, forwardProps, onClosedCallback }
        })
      }
      modalController.registerModal(modalName, handleModalUpdate, state.show)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const closeSelf = (callback) => modalController.closeModal(modalName, callback)

    const goToNextModal = (
      nextModalName,
      forwardProps = {},
      replaceMode = true
    ) => {
      if (replaceMode) {
        closeSelf(() =>
          modalController.showModal(nextModalName, {
            ...forwardProps,
            previousModal: modalName,
            replaceMode: true
          })
        )
      } else {
        modalController.showModal(nextModalName, {
          ...forwardProps,
          previousModal: modalName,
          replaceMode: false
        })
      }
    }

    const goToPreviousModal = (e, props = {}) => {
      closeSelf(
        () =>
          replaceMode.current &&
          previousModal.current &&
          modalController.showModal(previousModal.current, props)
      )
    }
    const onClosedCallback = () => {
      // nothing
    }

    return (
      <Modal
        {...ownProps}
        {...state.forwardProps}
        onClosed={
          typeof state.onClosedCallback !== 'function'
            ? onClosedCallback
            : state.onClosedCallback
        }
        modal={{
          show: state.show,
          onClose: closeSelf,
          onClosed:
            typeof state.onClosedCallback !== 'function'
              ? onClosedCallback
              : state.onClosedCallback
        }}
        goToPreviousModal={goToPreviousModal}
        goToNextModal={goToNextModal}
      />
    )
  }
  hoistNonReactStatic(ControlledModal, Modal)
  return ControlledModal
}

export const makeRouteControlledModal =
  (
    Modal,
    modalName: string,
    routeTrigger: (router) => boolean,
    clearRoute: (router) => void = NextRouter.back
  ) =>
  ({ show, ...props }: { show?: boolean }): JSX.Element => {
    const router = useRouter()
    const prevRoute = usePrevious(router)

    const [state, dispatch] = React.useReducer(modalReducer, {
      // eslint-disable-next-line react/prop-types
      show: show || routeTrigger(router)
    })

    const onClose = React.useCallback(
      (callback?) => {
        modalController.closeModal(modalName, () => {
          typeof callback === 'function' && callback()
          routeTrigger(router) && clearRoute(router)
        })
      },
      [router]
    )

    React.useEffect(() => {
      const handleModalUpdate = ({
        show,
        forwardProps,
        onClosedCallback = () => undefined
      }) =>
        dispatch({
          type: 'TOGGLE_SHOW_STATE',
          payload: { show, forwardProps, onClosedCallback }
        })
      modalController.registerModal(modalName, handleModalUpdate, state.show)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
      if (routeTrigger(router)) {
        modalController.showModal(modalName)
      } else if (routeTrigger(prevRoute)) {
        onClose()
      }
    }, [router, prevRoute, onClose])

    const modalProps = React.useMemo(
      () => ({
        show: state.show,
        onClose,
        onClosed: state.onClosedCallback
      }),
      [state.show, state.onClosedCallback, onClose]
    )

    return <Modal {...props} {...state.forwardProps} modal={modalProps} />
  }

export default modalController
